import api from "./api";

export const PUBLIC_CATALOGUE_PAGE_SIZE = 24;
export const PUBLIC_CATALOGUE_TIMEOUT_MS = 2500;
export const LEGACY_CATALOGUE_TIMEOUT_MS = 8000;
export const LEGACY_CATALOGUE_SNAPSHOT_ID = "legacy-live-compatibility";

const CACHE_KEY = "cc.public-catalogue.first-pages.v1";
const CACHE_VERSION = 2;
const MAX_CACHE_ENTRIES = 4;
const RETRY_DELAYS_MS = [300];
const FORBIDDEN_CACHE_KEY = /(^|_)(admin|internal|password|secret|token|notes?|raw)(_|$)/i;

const isObject = (value) => (
  value !== null && typeof value === "object" && !Array.isArray(value)
);

const clonePublicValue = (value) => {
  if (value == null || ["string", "number", "boolean"].includes(typeof value)) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(clonePublicValue);
  }
  if (!isObject(value)) return undefined;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !FORBIDDEN_CACHE_KEY.test(key))
      .map(([key, entry]) => [key, clonePublicValue(entry)])
      .filter(([, entry]) => entry !== undefined),
  );
};

const safeStorage = (storage) => {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const parseCache = (storage) => {
  const target = safeStorage(storage);
  if (!target) return { version: CACHE_VERSION, entries: {} };
  try {
    const parsed = JSON.parse(target.getItem(CACHE_KEY) || "null");
    if (parsed?.version !== CACHE_VERSION || !isObject(parsed?.entries)) {
      return { version: CACHE_VERSION, entries: {} };
    }
    return parsed;
  } catch {
    return { version: CACHE_VERSION, entries: {} };
  }
};

const normaliseString = (value) => String(value ?? "").trim();

const isCompleteCountFacet = (value, total) => (
  isObject(value)
  && Object.keys(value).length > 0
  && Object.values(value).every((count) => Number.isInteger(count) && count >= 0)
  && Object.values(value).reduce((sum, count) => sum + count, 0) === total
);

const countFacetsEqual = (left, right) => {
  if (!isObject(left) || !isObject(right)) return false;
  const keys = Object.keys(left);
  return keys.length === Object.keys(right).length
    && keys.every((key) => right[key] === left[key]);
};

export const catalogueQuery = (filters = {}) => ({
  q: normaliseString(filters.q),
  category: normaliseString(filters.category).toLowerCase(),
  operator_id: normaliseString(filters.operator_id),
  min_price: normaliseString(filters.min_price),
  max_price: normaliseString(filters.max_price),
  sort: normaliseString(filters.sort) || "overall",
  limit: PUBLIC_CATALOGUE_PAGE_SIZE,
});

export const catalogueQueryKey = (filters = {}) => JSON.stringify(catalogueQuery(filters));

export const isDefaultCatalogueQuery = (filters = {}) => {
  const query = catalogueQuery(filters);
  return (
    !query.q
    && !query.category
    && !query.operator_id
    && !query.min_price
    && !query.max_price
    && query.sort === "overall"
  );
};

export const DEFAULT_CATALOGUE_QUERY_KEY = catalogueQueryKey({});

const validateSummary = (value) => {
  const operatorBreakdown = value?.operator_breakdown;
  const facets = value?.facets;
  if (
    !isObject(value)
    || !normaliseString(value.snapshot_id)
    || !Number.isInteger(value.total)
    || value.total < 1
    || !isCompleteCountFacet(operatorBreakdown, value.total)
    || !isObject(facets)
    || !isCompleteCountFacet(facets.operators, value.total)
    || !isCompleteCountFacet(facets.categories, value.total)
    || !isCompleteCountFacet(facets.statuses, value.total)
    || !isObject(facets.price)
    || !countFacetsEqual(operatorBreakdown, facets.operators)
  ) {
    throw new Error("invalid_catalogue_summary");
  }
  return {
    snapshot_id: normaliseString(value.snapshot_id),
    published_at: value.published_at || null,
    canonical_hash: normaliseString(value.canonical_hash),
    total: value.total,
    operator_breakdown: clonePublicValue(operatorBreakdown),
    facets: clonePublicValue(facets),
  };
};

const validatePage = (value) => {
  if (
    !isObject(value)
    || !normaliseString(value.snapshot_id)
    || !Number.isInteger(value.total)
    || value.total < 1
    || !Array.isArray(value.items)
  ) {
    throw new Error("invalid_catalogue_page");
  }
  const items = value.items.map((item) => {
    if (
      !isObject(item)
      || !normaliseString(item.id)
      || !normaliseString(item.slug)
      || !normaliseString(item.title)
    ) {
      throw new Error("invalid_catalogue_item");
    }
    return clonePublicValue(item);
  });
  return {
    snapshot_id: normaliseString(value.snapshot_id),
    published_at: value.published_at || null,
    canonical_hash: normaliseString(value.canonical_hash),
    total: value.total,
    matching_total: Number.isInteger(value.matching_total) && value.matching_total >= 0
      ? value.matching_total
      : null,
    items,
    next_cursor: normaliseString(value.next_cursor) || null,
    has_more: Boolean(value.has_more && value.next_cursor),
    update_available: value.update_available === true,
  };
};

const validateDetail = (value) => {
  if (
    !isObject(value)
    || !normaliseString(value.snapshot_id)
    || !normaliseString(value.canonical_hash)
    || !isObject(value.item)
    || !normaliseString(value.item.id)
    || !normaliseString(value.item.slug)
    || !normaliseString(value.item.title)
  ) {
    throw new Error("invalid_catalogue_detail");
  }
  return {
    snapshot_id: normaliseString(value.snapshot_id),
    published_at: value.published_at || null,
    canonical_hash: normaliseString(value.canonical_hash),
    item: clonePublicValue(value.item),
    update_available: value.update_available === true,
  };
};

// Cache consumers outside the catalogue page use the same strict public-card
// boundary instead of trusting browser storage as valid API data.
export const normalisePublicCataloguePage = (value) => validatePage(value);

export const readCatalogueFirstPage = (queryKey, storage) => {
  const entry = parseCache(storage).entries[queryKey];
  if (!isObject(entry)) return null;
  try {
    const page = validatePage(entry.page);
    const summary = validateSummary(entry.summary);
    if (page.snapshot_id !== summary.snapshot_id || !normaliseString(entry.saved_at)) {
      return null;
    }
    return {
      page,
      summary,
      pageEtag: normaliseString(entry.page_etag) || null,
      summaryEtag: normaliseString(entry.summary_etag) || null,
      savedAt: entry.saved_at,
    };
  } catch {
    return null;
  }
};

export const writeCatalogueFirstPage = (
  queryKey,
  { page, summary, pageEtag = null, summaryEtag = null },
  storage,
) => {
  const target = safeStorage(storage);
  if (!target) return false;
  const cleanPage = validatePage(page);
  const cleanSummary = validateSummary(summary);
  if (cleanPage.snapshot_id !== cleanSummary.snapshot_id) return false;
  const cache = parseCache(target);
  const entry = {
    page: cleanPage,
    summary: cleanSummary,
    page_etag: normaliseString(pageEtag) || null,
    summary_etag: normaliseString(summaryEtag) || null,
    saved_at: new Date().toISOString(),
  };
  const entries = {
    ...cache.entries,
    [queryKey]: entry,
  };
  const retained = Object.fromEntries(
    Object.entries(entries)
      .sort((left, right) => String(right[1]?.saved_at).localeCompare(String(left[1]?.saved_at)))
      .slice(0, MAX_CACHE_ENTRIES),
  );
  try {
    target.setItem(CACHE_KEY, JSON.stringify({ version: CACHE_VERSION, entries: retained }));
    return true;
  } catch {
    return false;
  }
};

export const clearCatalogueFirstPageCache = (storage) => {
  const target = safeStorage(storage);
  try {
    target?.removeItem(CACHE_KEY);
  } catch {
    // Storage may be disabled. The network path remains usable.
  }
};

const abortError = () => {
  const error = new Error("catalogue_request_cancelled");
  error.name = "AbortError";
  return error;
};

const wait = (delay, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(abortError());
    return;
  }
  const timeout = setTimeout(resolve, delay);
  signal?.addEventListener("abort", () => {
    clearTimeout(timeout);
    reject(abortError());
  }, { once: true });
});

const statusFor = (error) => Number(error?.response?.status || 0);
const canRetry = (error, signal) => {
  if (signal?.aborted || error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
    return false;
  }
  const status = statusFor(error);
  return status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;
};

const requestPublicJson = async (
  path,
  config,
  {
    request = (url, options) => api.get(url, options),
    retryDelays = RETRY_DELAYS_MS,
    timeoutMs = PUBLIC_CATALOGUE_TIMEOUT_MS,
  } = {},
) => {
  let lastError;
  for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
    if (config.signal?.aborted) throw abortError();
    try {
      const response = await request(path, {
        ...config,
        publicOnly: true,
        timeout: timeoutMs,
        withCredentials: false,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });
      const cacheControl = String(
        response.headers?.["cache-control"] || response.headers?.["Cache-Control"] || "",
      );
      const cacheable = !/\b(private|no-store)\b/i.test(cacheControl);
      if (response.status === 304) {
        return { notModified: true, etag: config.etag || null, cacheable };
      }
      return {
        data: response.data,
        etag: response.headers?.etag || response.headers?.ETag || null,
        notModified: false,
        cacheable,
      };
    } catch (error) {
      lastError = error;
      if (attempt >= retryDelays.length || !canRetry(error, config.signal)) throw error;
      await wait(retryDelays[attempt], config.signal);
    }
  }
  throw lastError;
};

const responseHeader = (response, name) => normaliseString(
  response?.headers?.get?.(name)
  || response?.headers?.[name]
  || response?.headers?.[name.toLowerCase()],
);

const isCatalogueSnapshotNotReady = (error) => (
  statusFor(error) === 503
  && (
    error?.response?.data?.detail?.code === "catalogue_snapshot_not_ready"
    || responseHeader(error?.response, "retry-after") === "30"
  )
);

const fetchCatalogueReadinessState = async ({ signal, request } = {}) => {
  const result = await requestPublicJson(
    "/catalogue/v1/readiness",
    { signal },
    { request, retryDelays: [] },
  );
  if (
    result.notModified
    || !isObject(result.data)
    || typeof result.data.ready !== "boolean"
  ) {
    throw new Error("invalid_catalogue_readiness");
  }
  return result.data.ready;
};

const confirmCatalogueSnapshotNotReady = async (
  error,
  { signal, request, allowOpaque = false } = {},
) => {
  if (isCatalogueSnapshotNotReady(error)) return true;
  const status = statusFor(error);
  if (status !== 503 && !(status === 0 && allowOpaque)) return false;
  try {
    return !(await fetchCatalogueReadinessState({ signal, request }));
  } catch (readinessError) {
    if (
      signal?.aborted
      || readinessError?.name === "AbortError"
      || readinessError?.code === "ERR_CANCELED"
    ) {
      throw readinessError;
    }
    return false;
  }
};

const LEGACY_CURSOR_PREFIX = "legacy-offset:";

const legacyCursorFor = (offset) => `${LEGACY_CURSOR_PREFIX}${offset}`;

const legacyOffsetFor = (cursor) => {
  const raw = normaliseString(cursor);
  if (!raw.startsWith(LEGACY_CURSOR_PREFIX)) return null;
  const offset = Number(raw.slice(LEGACY_CURSOR_PREFIX.length));
  if (!Number.isSafeInteger(offset) || offset < 0) throw new Error("invalid_catalogue_cursor");
  return offset;
};

const legacyPageFrom = (payload, requestedOffset) => {
  const rows = Array.isArray(payload) ? payload : payload?.items;
  if (!Array.isArray(rows)) throw new Error("invalid_legacy_catalogue_page");
  const items = rows.map((item) => {
    if (
      !isObject(item)
      || !normaliseString(item.id)
      || !normaliseString(item.slug)
      || !normaliseString(item.title)
    ) {
      throw new Error("invalid_catalogue_item");
    }
    return clonePublicValue(item);
  });
  const reportedTotal = Number.isInteger(payload?.total) && payload.total >= 0
    ? payload.total
    : items.length;
  const requestedHasMore = Array.isArray(payload)
    ? false
    : (typeof payload?.has_more === "boolean"
      ? payload.has_more
      : requestedOffset + items.length < reportedTotal);
  const candidateOffset = Number.isSafeInteger(payload?.next_offset) && payload.next_offset >= 0
    ? payload.next_offset
    : requestedOffset + items.length;
  const hasMore = requestedHasMore && candidateOffset > requestedOffset;
  return validatePage({
    snapshot_id: LEGACY_CATALOGUE_SNAPSHOT_ID,
    published_at: null,
    canonical_hash: LEGACY_CATALOGUE_SNAPSHOT_ID,
    total: Math.max(reportedTotal, 1),
    matching_total: reportedTotal,
    items,
    next_cursor: hasMore ? legacyCursorFor(candidateOffset) : null,
    has_more: hasMore,
    update_available: false,
  });
};

const fetchLegacyCataloguePage = async ({
  filters,
  offset,
  signal,
  request,
  retryDelays,
}) => {
  const result = await requestPublicJson(
    "/competitions",
    {
      params: {
        paginated: true,
        ...Object.fromEntries(
          Object.entries(catalogueQuery(filters)).filter(([, value]) => value !== ""),
        ),
        offset,
      },
      signal,
    },
    { request, retryDelays, timeoutMs: LEGACY_CATALOGUE_TIMEOUT_MS },
  );
  if (result.notModified) throw new Error("legacy_catalogue_304_without_cache");
  return {
    page: legacyPageFrom(result.data, offset),
    etag: null,
    notModified: false,
    cacheable: false,
    legacyCompatibility: true,
  };
};

const fetchLegacyCatalogueDetail = async ({ slug, signal, request, retryDelays }) => {
  const result = await requestPublicJson(
    `/competitions/${encodeURIComponent(slug)}`,
    { signal },
    { request, retryDelays },
  );
  if (result.notModified) throw new Error("legacy_catalogue_detail_304_without_cache");
  return {
    detail: validateDetail({
      snapshot_id: LEGACY_CATALOGUE_SNAPSHOT_ID,
      published_at: null,
      canonical_hash: LEGACY_CATALOGUE_SNAPSHOT_ID,
      item: result.data,
      update_available: false,
    }),
    etag: null,
    cacheable: false,
    legacyCompatibility: true,
  };
};

const conditionalHeaders = (etag) => (
  etag ? { "If-None-Match": etag } : undefined
);

export const fetchCatalogueSummary = async ({ signal, cached = null, request, retryDelays } = {}) => {
  const result = await requestPublicJson(
    "/catalogue/v1/summary",
    {
      signal,
      etag: cached?.summaryEtag,
      headers: conditionalHeaders(cached?.summaryEtag),
    },
    { request, retryDelays },
  );
  if (result.notModified) {
    if (!cached?.summary) throw new Error("catalogue_summary_304_without_cache");
    return {
      summary: cached.summary,
      etag: cached.summaryEtag,
      notModified: true,
      cacheable: result.cacheable,
    };
  }
  return {
    summary: validateSummary(result.data),
    etag: result.etag,
    notModified: false,
    cacheable: result.cacheable,
  };
};

export const fetchCataloguePage = async ({
  filters = {},
  cursor = null,
  signal,
  cached = null,
  request,
  retryDelays,
} = {}) => {
  const legacyOffset = legacyOffsetFor(cursor);
  if (legacyOffset !== null) {
    return fetchLegacyCataloguePage({
      filters, offset: legacyOffset, signal, request, retryDelays,
    });
  }
  const requestQuery = catalogueQuery(filters);
  const params = {
    ...Object.fromEntries(Object.entries(requestQuery).filter(([, value]) => value !== "")),
    ...(cursor ? { cursor } : {}),
  };
  const useConditional = !cursor && cached?.pageEtag;
  try {
    const result = await requestPublicJson(
      "/catalogue/v1/competitions",
      {
        params,
        signal,
        etag: useConditional ? cached.pageEtag : null,
        headers: conditionalHeaders(useConditional ? cached.pageEtag : null),
      },
      { request, retryDelays },
    );
    if (result.notModified) {
      if (!cached?.page) throw new Error("catalogue_page_304_without_cache");
      return {
        page: cached.page,
        etag: cached.pageEtag,
        notModified: true,
        cacheable: result.cacheable,
      };
    }
    return {
      page: validatePage(result.data),
      etag: result.etag,
      notModified: false,
      cacheable: result.cacheable,
    };
  } catch (error) {
    if (cursor || !(await confirmCatalogueSnapshotNotReady(error, {
        signal,
        request,
        allowOpaque: !cached?.page,
      }))) {
      throw error;
    }
    return fetchLegacyCataloguePage({ filters, offset: 0, signal, request, retryDelays });
  }
};

export const fetchCatalogueDetail = async ({
  slug,
  snapshotId = null,
  allowSnapshotRefresh = false,
  signal,
  request,
  retryDelays,
} = {}) => {
  const cleanSlug = normaliseString(slug);
  const cleanSnapshotId = normaliseString(snapshotId);
  if (!cleanSlug) throw new Error("invalid_catalogue_slug");
  if (cleanSnapshotId === LEGACY_CATALOGUE_SNAPSHOT_ID) {
    return fetchLegacyCatalogueDetail({
      slug: cleanSlug, signal, request, retryDelays,
    });
  }
  let snapshotRecoveryEntered = false;
  try {
    const readDetail = (requestedSnapshot, detailRetryDelays = retryDelays) => requestPublicJson(
      `/catalogue/v1/competitions/${encodeURIComponent(cleanSlug)}`,
      {
        params: { snapshot_id: requestedSnapshot },
        signal,
      },
      { request, retryDelays: detailRetryDelays },
    );
    let refreshedSummary = null;
    let requestedSnapshot = cleanSnapshotId || (await fetchCatalogueSummary({ signal, request, retryDelays })).summary.snapshot_id;
    let result;
    try {
      result = await readDetail(requestedSnapshot);
    } catch (error) {
      if ((cleanSnapshotId && allowSnapshotRefresh !== true) || statusFor(error) !== 409
        || error?.response?.data?.detail?.code !== "catalogue_snapshot_changed") throw error;
      snapshotRecoveryEntered = true;
      // The detail page explicitly follows the latest catalogue after turnover.
      // This single recovery keeps the same slug/signal; neither recovery GET retries.
      refreshedSummary = (await fetchCatalogueSummary({ signal, request, retryDelays: [] })).summary;
      requestedSnapshot = refreshedSummary.snapshot_id;
      result = await readDetail(requestedSnapshot, []);
    }
    if (result.notModified) throw new Error("catalogue_detail_304_without_cache");
    const validatedDetail = validateDetail(result.data);
    if (refreshedSummary && (
      validatedDetail.snapshot_id !== refreshedSummary.snapshot_id
      || validatedDetail.canonical_hash !== refreshedSummary.canonical_hash
      || (validatedDetail.item.slug !== cleanSlug && validatedDetail.item.id !== cleanSlug)
    )) throw new Error("catalogue_detail_refresh_mismatch");
    return {
      detail: validatedDetail,
      etag: result.etag,
      cacheable: result.cacheable,
    };
  } catch (error) {
    if (
      cleanSnapshotId
      || snapshotRecoveryEntered
      || !(await confirmCatalogueSnapshotNotReady(error, { signal, request }))
    ) {
      throw error;
    }
    return fetchLegacyCatalogueDetail({
      slug: cleanSlug, signal, request, retryDelays,
    });
  }
};

const summaryFromPage = (page, fallback = null) => ({
  snapshot_id: page.snapshot_id,
  published_at: page.published_at,
  canonical_hash: page.canonical_hash,
  total: page.total,
  operator_breakdown: fallback?.snapshot_id === page.snapshot_id
    ? fallback.operator_breakdown
    : {},
  facets: fallback?.snapshot_id === page.snapshot_id ? fallback.facets : {},
});

export const fetchCatalogueFirstPage = async ({
  filters = {},
  signal,
  cached = null,
  request,
  retryDelays,
} = {}) => {
  const options = { signal, cached, request, retryDelays };
  // Start a fresh-client readiness probe before the immutable request can be
  // obscured by a browser/network boundary. Its result is advisory unless the
  // immutable page fails with an expected migration-class status.
  const readinessProbe = cached?.page
    ? null
    : fetchCatalogueReadinessState({ signal, request }).catch(() => null);
  let settledSummary = null;
  const summaryRefresh = fetchCatalogueSummary(options)
    .then((result) => {
      settledSummary = result;
      return result;
    })
    .catch(() => null);
  // The optional summary/facet refresh must never delay useful cards.
  let pageResponse;
  try {
    pageResponse = await fetchCataloguePage({ ...options, filters });
  } catch (error) {
    const status = statusFor(error);
    const readiness = readinessProbe ? await readinessProbe : null;
    if ((status !== 0 && status !== 503) || readiness !== false) throw error;
    pageResponse = await fetchLegacyCataloguePage({
      filters, offset: 0, signal, request, retryDelays,
    });
  }
  const remoteSummary = settledSummary?.summary?.snapshot_id === pageResponse.page.snapshot_id
    ? settledSummary
    : null;
  const cachedSummary = cached?.summary?.snapshot_id === pageResponse.page.snapshot_id
    ? cached.summary
    : null;
  const summary = remoteSummary?.summary
    || cachedSummary
    || summaryFromPage(pageResponse.page);
  // Rendering depends only on the complete immutable page response. Persistent
  // promotion is stricter: page and real summary metadata must identify the
  // same snapshot, so an empty synthetic facet set can never become the LKG.
  const completeSummaryIsCacheable = remoteSummary
    ? remoteSummary.cacheable
    : Boolean(cachedSummary);
  return {
    page: pageResponse.page,
    summary,
    pageEtag: pageResponse.etag,
    summaryEtag: remoteSummary?.etag || (cachedSummary ? cached?.summaryEtag : null),
    cacheable: pageResponse.cacheable && completeSummaryIsCacheable,
    pageCacheable: pageResponse.cacheable,
    summaryRefresh,
  };
};

export const isCatalogueAbort = (error) => (
  error?.name === "AbortError" || error?.code === "ERR_CANCELED"
);
